import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';

import { SharedLanguageService } from '@language';
import {
    AbstractGeoSandboxService,
    ApiQuery,
    Combine,
    DottedKey,
    mapPaginationInfo,
    PartialRecord,
    QueryPart,
    QueryPartsToStructuredQuery,
    RecordPhase,
    RecordRestrictions,
    relevanceSortInfo,
    Restriction,
    StructuredQuery,
    TernaryLogic,
    ZoneRestrictions,
    ZonesService
} from '@stylehaven/components';
import { SearchService } from '../../services/api/search.service';

@Injectable({ providedIn: 'root' })
export class GeoSandboxService extends AbstractGeoSandboxService {
    constructor(
        private lang: SharedLanguageService,
        private searchService: SearchService,
        private zonesService: ZonesService,
        private zoneRestrictions: ZoneRestrictions
    ) {
        super();
    }

    public translate(key: string): string {
        return this.lang.translate(key);
    }

    protected getRecords(spacialQuery: QueryPart): Observable<PartialRecord[]> {
        return this.getQuery(QueryPartsToStructuredQuery([spacialQuery])).pipe(
            switchMap(query => this.searchService.getRecords(mapPaginationInfo, relevanceSortInfo, query)),
            map(paginated => paginated.results)
        );
    }

    private getQuery(structuredQuery: StructuredQuery, fieldSelect?: DottedKey[]): Observable<ApiQuery> {
        return this.getRestrictions().pipe(
            map(restrictions => new ApiQuery(structuredQuery, restrictions, fieldSelect))
        );
    }

    protected getRestrictions(): Observable<Restriction> {
        // All objects search since no knowledge about context
        return this.zonesService.zones$.pipe(
            map(zones =>
                Combine.And(
                    this.zoneRestrictions.regularRecordTypesZone(),
                    RecordRestrictions.geolocationRestriction(TernaryLogic.true),
                    Combine.Or(
                        RecordRestrictions.phaseRestriction([RecordPhase.Published, RecordPhase.Archived]),
                        RecordRestrictions.conceptZoneRestriction(zones)
                    )
                )
            ),
            shareReplay(1)
        );
    }
}
